/**
 * Bounds how many HTML markers the 3D globe renders at once.
 *
 * Ported from Worldmonitor's globe-marker-budget utility (MIT — see
 * THIRD-PARTY-NOTICES.md).
 *
 * globe.gl renders `htmlElementsData` through three's CSS2DRenderer, which
 * walks EVERY marker on EVERY animation frame and rewrites `display`,
 * `transform` and `zIndex` on each one — unconditionally, even with a still
 * camera — then runs a whole-scene `traverseVisible` plus an O(n log n)
 * depth sort. Frame cost is therefore linear in marker count, so the
 * ceiling belongs here, not upstream.
 *
 * Deliberately free of DOM and three.js imports so the selection function
 * can be unit-tested directly against real payload shapes.
 */

/** A single layer's contribution to the globe, before budgeting. */
export interface GlobeMarkerGroup<T> {
  /**
   * Layer toggle key this group belongs to. Several groups may share one
   * key, in which case each is budgeted independently (separate feeds with
   * unrelated volumes) but their truncation is reported against the one key.
   */
  layer: string;
  markers: readonly T[];
  /**
   * Higher = more important = kept when the group must be trimmed. Layers
   * with a severity signal (deaths, magnitude, brightness) pass it here;
   * the rest get `proximityRank` so the cut is view-relative.
   */
  rank?: (marker: T) => number;
  /**
   * Breaks ties within an equal `rank`, before source order does. Pass
   * `proximityRank` so severity decides first and nearness decides the rest.
   */
  tieBreak?: (marker: T) => number;
  /**
   * Ephemeral or navigational markers that must always render. Exempt
   * groups bypass the budget entirely; keep them to a handful of markers.
   */
  exempt?: boolean;
}

export interface GlobeMarkerBudget {
  /** Ceiling per GROUP, not per layer key. */
  perLayer: number;
  /** Ceiling across all non-exempt layers combined. */
  total: number;
}

export interface GlobeLayerTruncation {
  shown: number;
  total: number;
}

export interface GlobeMarkerSelection<T> {
  markers: T[];
  /** Only layers that actually lost markers appear here. */
  truncated: Record<string, GlobeLayerTruncation>;
}

/** Mobile bites only when a user stacks heavy layers onto a phone. */
export const GLOBE_MARKER_BUDGET_MOBILE: GlobeMarkerBudget = { perLayer: 150, total: 400 };

/** Desktop ceiling — WM measured 2,319 markers uncapped, cut ~2.9x. */
export const GLOBE_MARKER_BUDGET_DESKTOP: GlobeMarkerBudget = { perLayer: 300, total: 800 };

/**
 * Largest per-group cap `c <= max` for which `sum(min(len_i, c)) <= total`.
 *
 * Trimming the biggest groups first (max-min fairness) rather than trimming
 * every group proportionally keeps small layers whole: one 1,500-marker
 * vessel feed must not evict an 11-marker weather layer.
 */
function fairShareCap(sizes: readonly number[], total: number, max: number): number {
  const used = (cap: number): number => sizes.reduce((sum, n) => sum + Math.min(n, cap), 0);
  if (used(max) <= total) return max;
  let lo = 0;
  let hi = max;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (used(mid) <= total) lo = mid; else hi = mid - 1;
  }
  return lo;
}

export interface LatLng { lat: number; lng: number }

/**
 * Ranks markers by nearness to the point the camera is looking at.
 *
 * The default for layers with no severity signal of their own — trimming a
 * static reference layer by raw array order would quietly lose whatever
 * sits past the cut. Nearness makes the trim view-relative instead of
 * arbitrary. The score is the cosine of the central angle (higher =
 * nearer), which orders identically to great-circle distance without the
 * `acos`.
 */
export function proximityRank<T>(focus: LatLng, position: (marker: T) => LatLng): (marker: T) => number {
  const rad = Math.PI / 180;
  const focusLat = focus.lat * rad;
  const sinFocusLat = Math.sin(focusLat);
  const cosFocusLat = Math.cos(focusLat);
  return (marker) => {
    const { lat, lng } = position(marker);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return -Infinity;
    const markerLat = lat * rad;
    return sinFocusLat * Math.sin(markerLat)
      + cosFocusLat * Math.cos(markerLat) * Math.cos((lng - focus.lng) * rad);
  };
}

/** Top `cap` markers by rank then tie-break, preserving the group's own order. */
function takeTop<T>(
  markers: readonly T[],
  cap: number,
  rank?: (marker: T) => number,
  tieBreak?: (marker: T) => number,
): T[] {
  if (markers.length <= cap) return markers.slice();
  // No rank means the caller has no opinion, so the cut falls wherever the
  // feed happened to order things. Callers rendering anything a user might
  // depend on should pass a rank rather than accept this.
  if (!rank) return markers.slice(0, cap);
  // A non-finite score sorts last rather than poisoning the comparator with NaN.
  const finite = (value: number): number => (Number.isFinite(value) ? value : -Infinity);
  // Rank desc, then tie-break desc, then original index asc — compared in
  // order rather than summed, so a coarse rank never has to be weighted
  // against a fine tie-break.
  const keep = markers
    .map((marker, index) => ({
      marker,
      index,
      score: finite(rank(marker)),
      tie: tieBreak ? finite(tieBreak(marker)) : 0,
    }))
    .sort((a, b) => (b.score - a.score) || (b.tie - a.tie) || (a.index - b.index))
    .slice(0, cap)
    .sort((a, b) => a.index - b.index);
  return keep.map(entry => entry.marker);
}

/**
 * Applies the per-layer cap, then the global ceiling, returning the markers
 * to render plus a report of what was withheld so the UI can disclose it.
 */
export function selectGlobeMarkers<T>(
  groups: readonly GlobeMarkerGroup<T>[],
  budget: GlobeMarkerBudget,
): GlobeMarkerSelection<T> {
  const exempt = groups.filter(group => group.exempt);
  const budgeted = groups.filter(group => !group.exempt && group.markers.length > 0);

  const cap = fairShareCap(
    budgeted.map(group => group.markers.length),
    budget.total,
    budget.perLayer,
  );

  const markers: T[] = [];
  // Totalled across every group sharing a layer key, so a layer whose
  // vessel feed is trimmed still reports its untrimmed flights in the same
  // figure.
  const perLayer = new Map<string, GlobeLayerTruncation>();

  for (const group of budgeted) {
    const kept = takeTop(group.markers, cap, group.rank, group.tieBreak);
    const running = perLayer.get(group.layer) ?? { shown: 0, total: 0 };
    running.shown += kept.length;
    running.total += group.markers.length;
    perLayer.set(group.layer, running);
    markers.push(...kept);
  }
  for (const group of exempt) {
    markers.push(...group.markers);
  }

  const truncated: Record<string, GlobeLayerTruncation> = {};
  for (const [layer, counts] of perLayer) {
    if (counts.shown < counts.total) truncated[layer] = counts;
  }

  return { markers, truncated };
}
